import noImage from "assets/images/no-image.jpg";
import { Fragment, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import useOrderQuery from "components/order/useOrderQuery";

const Order = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const { orders, pagination, loading, error } = useOrderQuery({
    page: currentPage,
  });

  const onError = useCallback((e) => {
    e.target.src = noImage;
  }, []);

  // ── Order status: number → string ────────────────────────────
  const getStatusText = useCallback((status) => {
    switch (status) {
      case 1:  return "Pending";
      case 2:  return "Processing";
      case 3:  return "Shipped";
      case 4:  return "Completed";
      case 5:  return "Cancelled";
      default: return "Unknown";
    }
  }, []);

  // ── Order status badge color ──────────────────────────────────
  const getStatusColor = useCallback((status) => {
    switch ((status || "").toLowerCase()) {
      case "completed":  return "success";
      case "processing": return "info";
      case "shipped":    return "primary";
      case "cancelled":  return "danger";
      case "pending":    return "warning";
      default:           return "secondary";
    }
  }, []);

  // ── Payment status badge color ────────────────────────────────
  // FIX: Payment status ke liye alag color function
  const getPaymentStatusColor = useCallback((paymentStatus) => {
    switch ((paymentStatus || "").toLowerCase()) {
      case "paid":    return "success";
      case "failed":  return "danger";
      case "pending": return "warning";
      case "refunded": return "info";
      default:        return "secondary";
    }
  }, []);

  // ── Pagination ────────────────────────────────────────────────
  const goToPage = useCallback(
    (page) => {
      if (
        page >= 1 &&
        page <= (pagination?.lastPage || 1) &&
        page !== currentPage
      ) {
        setCurrentPage(page);
      }
    },
    [pagination?.lastPage, currentPage]
  );

  // ── Loading ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Error loading orders: {error.message}
      </div>
    );
  }

  // ── Empty ─────────────────────────────────────────────────────
  if (!Array.isArray(orders) || orders.length === 0) {
    return (
      <div className="text-center py-5 empty-state">
        <h4>No orders found</h4>
        <Link to="/" className="btn btn-dark mt-3">
          Continue Shopping
        </Link>
      </div>
    );
  }

  // ── Pagination pages — max 5 dikhao ──────────────────────────
  const getPageNumbers = () => {
    const total   = pagination?.lastPage || 1;
    const current = pagination?.currentPage || 1;

    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // Current page ke aas paas 2 pages dikhao
    let start = Math.max(1, current - 2);
    let end   = Math.min(total, current + 2);

    if (start === 1) end   = Math.min(5, total);
    if (end === total) start = Math.max(1, total - 4);

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <Fragment>
      <div className="container-fluid order-container">
        <div className="account-order">
          <div className="order-grid-container">
            <div className="order-grid row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-4">
              {orders.map((order) => {

                const statusText    = getStatusText(order.status);
                const statusColor   = getStatusColor(statusText);
                const paymentStatus = order.payment_status
                const paymentColor  = getPaymentStatusColor(paymentStatus);

                return (
                  <div key={order.id} className="col">
                    <Link
                      to={`/orders-details/${order.id}`}
                      className="text-decoration-none order-link"
                    >
                      <div className="order-card">
                        <div className="order-card-header">
                          <h5>#{order.tracking_number || order.id}</h5>
                          
                        </div>

                        <div className="order-card-body">
                          <div className="order-detail">
                            <span className="detail-label">Date:</span>
                            <span>
                              {new Date(order.created_at).toLocaleDateString()}
                            </span>
                          </div>

                          <div className="order-detail">
                            <span className="detail-label">Total:</span>
                            {/* FIX: null total crash fix */}
                            <strong>₹{parseFloat(order.total || 0).toFixed(2)}</strong>
                          </div>

                          <div className="order-detail">
                            <span className="detail-label">Payment:</span>
                            {/* FIX: Payment status badge with color */}
                            <span className={`badge bg-${paymentColor}`}>
                              {paymentStatus}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Pagination */}
        {pagination?.lastPage > 1 && (
          <nav aria-label="Order pagination" className="mt-4">
            <ul className="pagination justify-content-center flex-wrap">

              {/* Previous */}
              <li className={`page-item ${pagination.currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => goToPage(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                >
                  Previous
                </button>
              </li>

              {/* First page + ellipsis */}
              {getPageNumbers()[0] > 1 && (
                <>
                  <li className="page-item">
                    <button className="page-link" onClick={() => goToPage(1)}>1</button>
                  </li>
                  {getPageNumbers()[0] > 2 && (
                    <li className="page-item disabled">
                      <span className="page-link">…</span>
                    </li>
                  )}
                </>
              )}

              {/* Page numbers */}
              {getPageNumbers().map((page) => (
                <li
                  key={page}
                  className={`page-item ${pagination.currentPage === page ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => goToPage(page)}>
                    {page}
                  </button>
                </li>
              ))}

              {/* Last page + ellipsis */}
              {getPageNumbers().slice(-1)[0] < pagination.lastPage && (
                <>
                  {getPageNumbers().slice(-1)[0] < pagination.lastPage - 1 && (
                    <li className="page-item disabled">
                      <span className="page-link">…</span>
                    </li>
                  )}
                  <li className="page-item">
                    <button
                      className="page-link"
                      onClick={() => goToPage(pagination.lastPage)}
                    >
                      {pagination.lastPage}
                    </button>
                  </li>
                </>
              )}

              {/* Next */}
              <li
                className={`page-item ${
                  pagination.currentPage === pagination.lastPage ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => goToPage(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.lastPage}
                >
                  Next
                </button>
              </li>
            </ul>

            <div className="text-center text-muted pagination-info mt-2">
              Showing{" "}
              {(pagination.currentPage - 1) * pagination.perPage + 1} to{" "}
              {Math.min(
                pagination.currentPage * pagination.perPage,
                pagination.total
              )}{" "}
              of {pagination.total} orders
            </div>
          </nav>
        )}
      </div>
    </Fragment>
  );
};

export default Order;